/**
 * @file AdminReferrals.jsx — /cx/referidos
 */
import React, { useState, useEffect } from "react";
import {
  Users,
  Hourglass,
  Trophy,
  TimerOff,
  Gift,
} from "lucide-react";
import { adminService } from "../services/admin";
import { ADMIN_STRINGS as S } from "../constants/strings";
import {
  Card,
  Chip,
  Spinner,
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
} from "../ui";
import "./AdminGeneric.css";

const STATUS_TONES = { PENDING: "warn", COMPLETED: "ok", EXPIRED: "neutral" };
const STATUS_LABELS = {
  PENDING: S.REFERRALS_PENDING,
  COMPLETED: S.REFERRALS_COMPLETED,
  EXPIRED: S.REFERRALS_EXPIRED,
};

function StatCard({ icon, label, value, tone = "primary" }) {
  return (
    <Card className="adm-stat">
      <span className={`adm-stat-icon adm-stat-icon-${tone}`}>{icon}</span>
      <span className="adm-stat-value">{value}</span>
      <span className="adm-stat-label">{label}</span>
    </Card>
  );
}

const formatName = (pro) =>
  pro?.user ? `${pro.user.firstName} ${pro.user.lastName}` : "—";
const formatEmail = (pro) => pro?.user?.email || "—";
const formatDate = (date) =>
  date
    ? new Date(date).toLocaleDateString("es-MX", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

export default function AdminReferrals() {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    adminService
      .getReferrals()
      .then((res) => setData(res.data))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  if (isLoading) {
    return (
      <div className="ag-page">
        <div className="ag-loading">
          <Spinner size={28} />
        </div>
      </div>
    );
  }

  const { referrals = [], stats = {} } = data || {};

  return (
    <div className="ag-page">
      <header className="ag-head">
        <span className="ag-eyebrow">Admin · Referidos</span>
        <h1 className="ag-title">{S.REFERRALS_TITLE}</h1>
      </header>

      <div className="adm-stat-grid">
        <StatCard icon={<Users size={18} />} label={S.REFERRALS_TOTAL} value={stats.total || 0} tone="primary" />
        <StatCard icon={<Hourglass size={18} />} label={S.REFERRALS_PENDING} value={stats.pending || 0} tone="warn" />
        <StatCard icon={<Trophy size={18} />} label={S.REFERRALS_COMPLETED} value={stats.completed || 0} tone="ok" />
        <StatCard icon={<TimerOff size={18} />} label={S.REFERRALS_EXPIRED} value={stats.expired || 0} tone="primary" />
      </div>

      {referrals.length === 0 ? (
        <Card className="ag-empty">
          <Gift size={36} style={{ color: "var(--ink-4)", marginBottom: 8 }} />
          <p>{S.REFERRALS_EMPTY}</p>
        </Card>
      ) : (
        <Card padded={false} className="ag-table-wrap">
          <Table>
            <THead>
              <TR>
                <TH>{S.REFERRALS_REFERRER}</TH>
                <TH>{S.REFERRALS_REFERRED}</TH>
                <TH>{S.REFERRALS_STATUS}</TH>
                <TH>{S.REFERRALS_DATE}</TH>
                <TH>{S.REFERRALS_REWARD_DATE}</TH>
              </TR>
            </THead>
            <TBody>
              {referrals.map((r) => (
                <TR key={r.id}>
                  <TD>
                    <div className="aux-contact">
                      <span style={{ fontWeight: 500 }}>{formatName(r.referrerProfessional)}</span>
                      <span className="aux-contact-sub">{formatEmail(r.referrerProfessional)}</span>
                    </div>
                  </TD>
                  <TD>
                    <div className="aux-contact">
                      <span style={{ fontWeight: 500 }}>{formatName(r.referredProfessional)}</span>
                      <span className="aux-contact-sub">{formatEmail(r.referredProfessional)}</span>
                    </div>
                  </TD>
                  <TD>
                    <Chip tone={STATUS_TONES[r.status] || "neutral"} size="sm">
                      {STATUS_LABELS[r.status] || r.status}
                    </Chip>
                  </TD>
                  <TD>{formatDate(r.createdAt)}</TD>
                  <TD>{formatDate(r.rewardAppliedAt)}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
